import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { createPaymentOrder, verifyPayment, simulateTestPayment, getPublicShopSettings } from '../utils/api';
import {
  submitShopInquiryFromCart,
  openShopInquiryWhatsAppFromCart,
} from '../utils/shopInquirySubmit';
import { useShopLanguage } from '../context/ShopLanguageContext';
import { cn } from '../lib/utils';
import { roundRupees } from '../utils/money';
import { readShopCart, writeShopCart, clearShopCart } from '../utils/shopCartStorage';
import { buildRazorpayCheckoutOptions } from '../utils/razorpayCheckout';
import { completePublicGalleryOrderAfterPayment } from '../utils/shopPaymentFlow';
import { downloadShopOrderReceipt } from '../utils/orderReceiptDownload';
import ShopToaster from './ShopToaster';
import ShopOrderFlowOverlay from './ShopOrderFlowOverlay';
import { sectionVariants, usePrefersReducedMotion } from '../lib/motionShared';
import { useIsSmUp } from '../hooks/useIsSmUp';
import {
  formatModelCode,
  modelMatchesCodeSearch,
  parseModelCodeQuery,
} from '../utils/modelCategoryRanges';
import {
  getShopLineUnitPrice,
  getShopDisplayUnitPrice,
  calculateShopCartTotal,
  getShopMinimumAdvance,
} from '../utils/shopOrderType';
import { getGalleryStockLabel, isShopItemOutOfStock } from '../utils/shopStockLabel';

// Public API calls (no auth required)
const getPublicCategories = async () => {
  const response = await fetch('/api/inventory/public/categories');
  if (!response.ok) throw new Error('Failed to fetch categories');
  return response.json();
};

const getPublicInventory = async (category = null) => {
  const url = category
    ? `/api/inventory/public?category=${category}`
    : '/api/inventory/public';
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch inventory');
  return response.json();
};

const createPublicOrder = async (orderData) => {
  const response = await fetch('/api/orders/public', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orderData)
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create order');
  }
  return response.json();
};

const uiTokens = {
  buttonPrimary:
    'inline-flex items-center justify-center bg-accent text-accent-foreground rounded-full font-semibold shadow-sm hover:bg-accent/90 transition-colors',
  buttonSecondary:
    'inline-flex items-center justify-center border border-border bg-card text-foreground rounded-xl font-semibold hover:bg-muted transition-colors',
  buttonGhost:
    'inline-flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted/80 rounded-xl transition-colors',
};


const omSymbolSvgUrl = 'https://upload.wikimedia.org/wikipedia/commons/6/67/Aum_Om_red.svg';

const THEMES = [
  {
    id: 'marigold',
    name: 'Marigold Blessing',
    // Golden yellow → saffron-orange
    gradient: 'linear-gradient(135deg, #c8860a 0%, #e05c00 100%)',
    gradientSubtle: 'linear-gradient(135deg, #fef9ec 0%, #fff3e0 100%)',
    gradientRgb: '200, 134, 10',
    symbolFilter: 'hue-rotate(28deg) saturate(1.3) brightness(1.05)',
    vars: {
      '--background':          '254 249 236',
      '--foreground':          '44 28 6',
      '--card':                '255 253 245',
      '--card-foreground':     '44 28 6',
      '--primary':             '200 134 10',
      '--primary-foreground':  '255 255 255',
      '--secondary':           '253 236 190',
      '--secondary-foreground':'140 88 0',
      '--muted':               '253 236 190',
      '--muted-foreground':    '140 108 40',
      '--accent':              '224 92 0',
      '--accent-foreground':   '255 255 255',
      '--border':              '245 218 160',
      '--input':               '245 218 160',
      '--ring':                '200 134 10',
    },
  },
  {
    id: 'sindoor',
    name: 'Sindoor Puja',
    // Deep vermillion → rose-red
    gradient: 'linear-gradient(135deg, #b02020 0%, #d94f3a 100%)',
    gradientSubtle: 'linear-gradient(135deg, #fff5f5 0%, #fff0ee 100%)',
    gradientRgb: '176, 32, 32',
    symbolFilter: 'hue-rotate(0deg) saturate(1.5) brightness(0.9)',
    vars: {
      '--background':          '255 248 247',
      '--foreground':          '40 14 14',
      '--card':                '255 253 252',
      '--card-foreground':     '40 14 14',
      '--primary':             '176 32 32',
      '--primary-foreground':  '255 255 255',
      '--secondary':           '254 224 218',
      '--secondary-foreground':'140 30 30',
      '--muted':               '254 224 218',
      '--muted-foreground':    '160 80 70',
      '--accent':              '217 79 58',
      '--accent-foreground':   '255 255 255',
      '--border':              '248 202 196',
      '--input':               '248 202 196',
      '--ring':                '176 32 32',
    },
  },
  {
    id: 'tulsi',
    name: 'Tulsi Vana',
    // Forest green → emerald
    gradient: 'linear-gradient(135deg, #1a5c3a 0%, #2e8b57 100%)',
    gradientSubtle: 'linear-gradient(135deg, #f2faf5 0%, #eaf7ee 100%)',
    gradientRgb: '26, 92, 58',
    symbolFilter: 'hue-rotate(118deg) saturate(1.4) brightness(0.88)',
    vars: {
      '--background':          '242 250 245',
      '--foreground':          '10 36 20',
      '--card':                '250 255 252',
      '--card-foreground':     '10 36 20',
      '--primary':             '26 92 58',
      '--primary-foreground':  '255 255 255',
      '--secondary':           '200 238 216',
      '--secondary-foreground':'18 72 44',
      '--muted':               '200 238 216',
      '--muted-foreground':    '60 120 80',
      '--accent':              '46 139 87',
      '--accent-foreground':   '255 255 255',
      '--border':              '174 222 196',
      '--input':               '174 222 196',
      '--ring':                '26 92 58',
    },
  },
  {
    id: 'panchavarni',
    name: 'Panchavarni',
    // All five Ganesha colors: yellow → red → green
    gradient: 'linear-gradient(135deg, #c8860a 0%, #c03030 50%, #1a6640 100%)',
    gradientSubtle: 'linear-gradient(135deg, #fdf8ec 0%, #fff4f0 50%, #f0faf4 100%)',
    gradientRgb: '180, 60, 20',
    symbolFilter: 'hue-rotate(15deg) saturate(1.2) brightness(1.0)',
    vars: {
      '--background':          '253 249 242',
      '--foreground':          '30 22 10',
      '--card':                '255 253 248',
      '--card-foreground':     '30 22 10',
      '--primary':             '180 60 20',
      '--primary-foreground':  '255 255 255',
      '--secondary':           '250 226 196',
      '--secondary-foreground':'130 50 10',
      '--muted':               '250 226 196',
      '--muted-foreground':    '130 100 60',
      '--accent':              '200 134 10',
      '--accent-foreground':   '255 255 255',
      '--border':              '240 214 186',
      '--input':               '240 214 186',
      '--ring':                '180 60 20',
    },
  },
];

const HERO_SLIDES = [
  ['/collage/1.jpg', '/collage/2.jpg', '/collage/3.jpg', '/collage/4.jpg', '/collage/5.jpg'],
  ['/collage/6.jpg', '/collage/1.jpg', '/collage/4.jpg', '/collage/2.jpg', '/collage/6.jpg'],
  ['/collage/3.jpg', '/collage/5.jpg', '/collage/2.jpg', '/collage/6.jpg', '/collage/4.jpg'],
];

const ShopPage = () => {
  const navigate = useNavigate();
  const { locale, setLocale, t } = useShopLanguage();
  const itemName = (item) => {
    if (locale === 'mr' && item?.modelNameMr) return item.modelNameMr;
    if (locale === 'kn' && item?.modelNameKn) return item.modelNameKn;
    return item?.modelName;
  };
  const itemSize = (item) => item?.size ?? item?.category;
  const categoryLabel = (categoryId, short = false) => {
    const keyMap = {
      small: short ? 'filters.categorySmallShort' : 'filters.categorySmall',
      medium: short ? 'filters.categoryMediumShort' : 'filters.categoryMedium',
      large: short ? 'filters.categoryLargeShort' : 'filters.categoryLarge',
      pen: short ? 'filters.categoryPenShort' : 'filters.categoryPen',
    };
    return keyMap[categoryId] ? t(keyMap[categoryId]) : categoryId;
  };
  const reduceMotion = usePrefersReducedMotion();
  const isSmUp = useIsSmUp();
  const [scrolled, setScrolled] = useState(false);
  const sectionMotion = useMemo(
    () =>
      reduceMotion
        ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
        : sectionVariants,
    [reduceMotion]
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.75);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [heroSlide, setHeroSlide] = useState(0);
  const galleryRef = useRef(null);
  const shopRootRef = useRef(null);
  const headerRef = useRef(null);
  const sizeBarSentinelRef = useRef(null);
  const [sizeBarStuck, setSizeBarStuck] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setHeroSlide(s => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const header = headerRef.current;
    const root = shopRootRef.current;
    if (!header || !root) return;

    const syncHeaderOffset = () => {
      root.style.setProperty('--shop-header-offset', `${header.offsetHeight}px`);
    };

    syncHeaderOffset();
    const observer = new ResizeObserver(syncHeaderOffset);
    observer.observe(header);
    window.addEventListener('resize', syncHeaderOffset);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncHeaderOffset);
    };
  }, [scrolled]);

  useEffect(() => {
    const sentinel = sizeBarSentinelRef.current;
    if (!sentinel) return;

    const headerOffset = headerRef.current?.offsetHeight ?? 80;
    const stuckObserver = new IntersectionObserver(
      ([entry]) => setSizeBarStuck(!entry.isIntersecting),
      { root: null, rootMargin: `-${headerOffset}px 0px 0px 0px`, threshold: 0 }
    );

    stuckObserver.observe(sentinel);
    return () => stuckObserver.disconnect();
  }, [scrolled]);

  const [activeTheme, setActiveTheme] = useState('marigold');

  // Restore saved theme on mount
  useEffect(() => {
    const saved = localStorage.getItem('shopTheme');
    if (saved && THEMES.find(t => t.id === saved)) {
      setActiveTheme(saved);
    }
  }, []);

  // Apply CSS variable overrides whenever activeTheme changes
  useEffect(() => {
    const theme = THEMES.find(t => t.id === activeTheme);
    if (!theme) return;
    const root = document.documentElement;
    Object.entries(theme.vars).forEach(([key, val]) => root.style.setProperty(key, val));
    localStorage.setItem('shopTheme', activeTheme);
  }, [activeTheme]);

  const activeThemeObj = THEMES.find(t => t.id === activeTheme) || THEMES[0];

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [allInventory, setAllInventory] = useState([]); // Store all items for filtering
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const isFirstLoad = useRef(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [razorpayTestMode, setRazorpayTestMode] = useState(false);
  const [shopPaymentEnabled, setShopPaymentEnabled] = useState(null);
  const [catalogUpdatingBannerEnabled, setCatalogUpdatingBannerEnabled] = useState(false);
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [simulatingPayment, setSimulatingPayment] = useState(false);
  const [orderFlowPhase, setOrderFlowPhase] = useState(null);
  const [completedOrderResult, setCompletedOrderResult] = useState(null);
  const [completedInquiryResult, setCompletedInquiryResult] = useState(null);
  const [cart, setCart] = useState([]);
  const [checkoutForm, setCheckoutForm] = useState({
    customerName: '',
    phone: '',
    deliveryDate: '',
    advanceAmount: 0,
    customRequest: ''
  });
  const [itemForm, setItemForm] = useState({
    quantity: 1,
    customRequest: ''
  });
  const [cartExpanded, setCartExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('model');
  const [wishlist, setWishlist] = useState([]);
  const [showWishlistModal, setShowWishlistModal] = useState(false);
  const [activeCardId, setActiveCardId] = useState(null);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const displayItems = allInventory;
  const catalogEmpty = !loading && allInventory.length === 0;
  const catalogUpdating =
    !loading && allInventory.length > 0 && catalogUpdatingBannerEnabled;

  const sizePillClass = (isActive) => cn(
    'h-7 sm:h-8 inline-flex items-center justify-center shrink-0',
    'px-1.5 sm:px-2 text-[11px] sm:text-xs font-medium rounded-lg whitespace-nowrap',
    'transition-all duration-200 border',
    isActive
      ? 'bg-primary text-primary-foreground border-primary shadow-sm ring-1 ring-inset ring-primary/40'
      : 'bg-white border-border text-foreground hover:border-primary/40 hover:bg-muted/30'
  );

  const scrollToGallery = () => {
    const filterBar = document.getElementById('gallery');
    if (!filterBar) return;
    const headerH = headerRef.current?.offsetHeight ?? 80;
    // Extra upward offset so the wholesale banner (above the filter bar) remains visible
    const extraOffset = 160;
    const top = filterBar.getBoundingClientRect().top + window.scrollY - headerH - extraOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const omParticles = useMemo(() => {
    const cols = 7;
    const rows = 4; // 7×4 = 28 cells — one particle per cell, no overlaps
    const cellW = 92 / cols;
    const cellH = 84 / rows;
    return Array.from({ length: cols * rows }, (_, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      return {
        id: `om-${index}`,
        top:  6  + row * cellH + Math.random() * (cellH * 0.7),
        left: 4  + col * cellW + Math.random() * (cellW * 0.7),
        size: 16 + Math.random() * 16,
        duration: 14 + Math.random() * 10,
        delay: -(Math.random() * 20),
        driftX: -14 + Math.random() * 28,
      };
    });
  }, []);
  const clearAllFilters = () => {
    setSearchQuery('');
    setPriceRange({ min: '', max: '' });
    setSortBy('price-low');
  };

  const handleImageMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({ x, y });
  };

  // Load cart and wishlist data on component mount
  useEffect(() => {
    const parsedCart = readShopCart();
    if (parsedCart.length > 0) {
      setCart(parsedCart);
      toast.success(t('toast.cartRestored'), {
        duration: 3000,
        icon: '🛒'
      });
    }

    const savedWishlist = localStorage.getItem('shopWishlist');
    if (savedWishlist) {
      try {
        const parsedWishlist = JSON.parse(savedWishlist);
        if (Array.isArray(parsedWishlist)) {
          setWishlist(parsedWishlist);
        }
      } catch (error) {
        console.error('Error parsing saved wishlist data:', error);
        localStorage.removeItem('shopWishlist'); // Clear corrupted data
      }
    }
    // Intentionally run once on mount; locale comes from initial localStorage + first paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch('/api/payments/test')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.testMode) setRazorpayTestMode(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getPublicShopSettings()
      .then((data) => {
        setShopPaymentEnabled(Boolean(data.shopPaymentEnabled));
        setCatalogUpdatingBannerEnabled(Boolean(data.catalogUpdatingBannerEnabled));
      })
      .catch(() => {
        setShopPaymentEnabled(false);
        setCatalogUpdatingBannerEnabled(false);
      });
  }, []);

  // Save cart data to sessionStorage whenever cart changes
  useEffect(() => {
    writeShopCart(cart);
  }, [cart]);

  // Save wishlist data to localStorage whenever wishlist changes
  useEffect(() => {
    if (wishlist.length > 0) {
      localStorage.setItem('shopWishlist', JSON.stringify(wishlist));
    } else {
      localStorage.removeItem('shopWishlist');
    }
  }, [wishlist]);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await getPublicCategories();
        setCategories(cats);
      } catch (error) {
        toast.error(t('toast.loadCategoriesFailed'));
        console.error(error);
      }
    };
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load inventory when category changes (null = all models)
  useEffect(() => {
    loadInventory(selectedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadInventory closes over fresh `t` each render
  }, [selectedCategory]);

  // Filter and sort inventory based on search and filters
  useEffect(() => {
    let filtered = [...inventory];

    // Apply search filter
    if (searchQuery.trim()) {
      const parsed = parseModelCodeQuery(searchQuery);
      const queryLower = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.modelName.toLowerCase().includes(queryLower) ||
        modelMatchesCodeSearch(itemSize(item), item.modelNumber, parsed) ||
        formatModelCode(itemSize(item), item.modelNumber).toLowerCase().includes(queryLower)
      );
    }

    // Apply price range filter
    if (priceRange.min) {
      filtered = filtered.filter(item => item.price >= parseFloat(priceRange.min));
    }
    if (priceRange.max) {
      filtered = filtered.filter(item => item.price <= parseFloat(priceRange.max));
    }

    // Apply sorting — model number keeps true sequence; price sorts put in-stock first
    filtered.sort((a, b) => {
      if (sortBy === 'model') {
        return (a.modelNumber ?? 0) - (b.modelNumber ?? 0);
      }
      const aOos = isShopItemOutOfStock(a);
      const bOos = isShopItemOutOfStock(b);
      if (aOos !== bOos) return aOos ? 1 : -1;
      if (sortBy === 'price-high') {
        return b.price - a.price;
      }
      return a.price - b.price;
    });

    setAllInventory(filtered);
  }, [inventory, searchQuery, priceRange, sortBy]);

  const loadInventory = async (category) => {
    try {
      setLoading(true);
      const items = await getPublicInventory(category);
      setInventory(items);
    } catch (error) {
      toast.error(t('toast.loadModelsFailed'));
      console.error(error);
    } finally {
      setLoading(false);
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        setPageLoading(false);
        if (sessionStorage.getItem('shopReturnToGallery')) {
          sessionStorage.removeItem('shopReturnToGallery');
          setTimeout(scrollToGallery, 950);
        }
      }
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleItemClick = (item) => {
    const effectiveCategory = selectedCategory ?? (item.size ?? item.category);
    sessionStorage.setItem('shopReturnToGallery', '1');
    sessionStorage.setItem('shopRelatedCategory', effectiveCategory);
    navigate(`/shop/${item.id}`, { state: { relatedCategory: effectiveCategory } });
  };

  const openAddToCartModal = (item) => {
    if (isShopItemOutOfStock(item)) {
      toast.error(t('gallery.stockOutOfStock'));
      return;
    }
    setSelectedItem(item);
    setItemForm({ quantity: 1, customRequest: '' });
    setShowModal(true);
  };

  const handleOpenAddToCart = (item, e) => {
    e.stopPropagation();
    openAddToCartModal(item);
  };

  const renderGalleryBadges = (item) => {
    const outOfStock = isShopItemOutOfStock(item);
    const isLowStock = !outOfStock && (item.stockStatus === 'low' || (item.currentStock ?? 0) <= 5);
    return (
    <>
      {item.retailOnly && (
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
          {t('gallery.retailOnlyBadge')}
        </div>
      )}
      <div className={cn(
        'absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium',
        outOfStock
          ? 'bg-muted text-muted-foreground border border-border'
          : isLowStock
          ? 'bg-rose-100 text-rose-700 border border-rose-200'
          : 'border border-border/60 bg-card/90 text-foreground'
      )}>
        {getGalleryStockLabel(item, t)}
      </div>
      <span className="absolute bottom-2 left-2 text-xs font-bold text-white bg-gray-900/80 px-1.5 py-0.5 rounded pointer-events-none">
        {formatModelCode(itemSize(item), item.modelNumber)}
      </span>
    </>
  );
  };

  const renderGalleryFooter = (item) => {
    const outOfStock = isShopItemOutOfStock(item);
    return (
    <div className="p-2.5" onClick={() => handleItemClick(item)}>
      <h3 className="font-semibold text-sm text-foreground line-clamp-2 mb-2 leading-snug">
        {itemName(item)}
      </h3>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold text-primary">
          ₹{roundRupees(getShopDisplayUnitPrice(item, 'retail')).toLocaleString('en-IN')}
        </p>
        {outOfStock ? (
          <button
            type="button"
            disabled
            className="flex-shrink-0 rounded-full border border-border bg-muted px-3 py-1.5 text-[11px] font-semibold text-muted-foreground cursor-not-allowed opacity-70"
          >
            {t('gallery.outOfStockLocked')}
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => handleOpenAddToCart(item, e)}
            className="flex-shrink-0 rounded-full bg-accent px-3 py-1.5 text-[11px] font-semibold text-accent-foreground hover:bg-accent/90 transition-colors"
          >
            {t('gallery.addToCartTitle')}
          </button>
        )}
      </div>
    </div>
  );
  };

  const shopOrderType = 'retail';
  const cartLineUnitPrice = (cartItem) => getShopLineUnitPrice(cartItem, shopOrderType);

  const calculateGrandTotal = () => calculateShopCartTotal(cart, shopOrderType);

  const calculateTotalAdvance = () => {
    return roundRupees(cart.reduce((sum, item) => sum + item.advanceAmount, 0));
  };

  const handleAddToCart = () => {
    const quantity = 1;

    if (isShopItemOutOfStock(selectedItem)) {
      toast.error(t('gallery.stockOutOfStock'));
      return;
    }

    if (cart.some((item) => item.id === selectedItem.id)) {
      toast.error(t('toast.itemAlreadyInCart'));
      return;
    }

    if (quantity > selectedItem.currentStock) {
      toast.error(t('toast.notEnoughStock'));
      return;
    }

    const cartItem = {
      ...selectedItem,
      quantity,
      customRequest: itemForm.customRequest,
      // Customer details will be added during checkout
      advanceAmount: 0, // Will be set during checkout
      customerName: '',
      phone: '',
      deliveryDate: ''
    };

    setCart(prev => [...prev, cartItem]);
    setShowModal(false);
    setSelectedItem(null);
    toast.success(t('toast.itemAdded'));
  };

  const handleCheckout = () => {
    const totalAmount = calculateGrandTotal();
    const minimumAdvance = getShopMinimumAdvance(totalAmount);

    setCheckoutForm({
      customerName: '',
      phone: '',
      deliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from now
      advanceAmount: minimumAdvance // Minimum 40% advance required
    });
    setShowCheckoutModal(true);
  };

  const prepareInquiryCart = () => {
    if (!checkoutForm.customerName.trim()) {
      toast.error(t('toast.nameRequired'));
      return null;
    }
    if (!checkoutForm.phone.trim() || !/^[6-9]\d{9}$/.test(checkoutForm.phone.trim())) {
      toast.error(t('toast.phoneRequired'));
      return null;
    }

    const updatedCart = cart.map((item) => ({
      ...item,
      customerName: checkoutForm.customerName,
      phone: checkoutForm.phone,
      deliveryDate: checkoutForm.deliveryDate,
      advanceAmount: 0,
      customRequest: checkoutForm.customRequest || item.customRequest || '',
    }));

    return { updatedCart };
  };

  const handleInquirySubmit = async () => {
    const prepared = prepareInquiryCart();
    if (!prepared) return;

    const { updatedCart } = prepared;

    try {
      setSubmittingInquiry(true);
      setOrderFlowPhase('inquirySubmitting');
      const inquiry = await submitShopInquiryFromCart({
        cart: updatedCart,
        customerName: checkoutForm.customerName,
        phone: checkoutForm.phone,
        locale,
        orderType: shopOrderType,
      });
      setCompletedInquiryResult({
        inquiry,
        cart: updatedCart,
        customerName: checkoutForm.customerName,
        phone: checkoutForm.phone,
        deliveryDate: checkoutForm.deliveryDate,
      });
      setCart([]);
      clearShopCart();
      setShowCheckoutModal(false);
      setOrderFlowPhase('inquirySuccess');
    } catch (error) {
      setOrderFlowPhase(null);
      setCompletedInquiryResult(null);
      toast.error(error.message || t('toast.inquiryFailed'));
    } finally {
      setSubmittingInquiry(false);
    }
  };

  const handleCheckoutSubmit = () => {
    if (!shopPaymentEnabled) {
      handleInquirySubmit();
      return;
    }

    const prepared = prepareCheckoutCart();
    if (!prepared) return;

    const { updatedCart, totalAdvance } = prepared;
    setCart(updatedCart);
    setShowCheckoutModal(false);
    handlePayment(totalAdvance, updatedCart);
  };

  const prepareCheckoutCart = () => {
    if (!checkoutForm.customerName.trim()) {
      toast.error(t('toast.nameRequired'));
      return null;
    }
    if (!checkoutForm.phone.trim() || !/^[6-9]\d{9}$/.test(checkoutForm.phone.trim())) {
      toast.error(t('toast.phoneRequired'));
      return null;
    }

    const totalAmount = calculateGrandTotal();
    const minimumAdvance = getShopMinimumAdvance(totalAmount);
    const totalAdvance = roundRupees(checkoutForm.advanceAmount);

    if (totalAdvance < minimumAdvance) {
      toast.error(
        t('toast.advanceMin', { amount: minimumAdvance.toLocaleString('en-IN') })
      );
      return null;
    }
    if (totalAdvance > totalAmount) {
      toast.error(t('toast.advanceExceeds'));
      return null;
    }

    const updatedCart = cart.map(item => ({
      ...item,
      customerName: checkoutForm.customerName,
      phone: checkoutForm.phone,
      deliveryDate: checkoutForm.deliveryDate,
      advanceAmount: Math.floor(totalAdvance / cart.length),
      customRequest: checkoutForm.customRequest || item.customRequest || ''
    }));

    const totalDistributed = updatedCart.slice(0, -1).reduce((sum, item) => sum + item.advanceAmount, 0);
    updatedCart[updatedCart.length - 1].advanceAmount = roundRupees(totalAdvance - totalDistributed);

    return { updatedCart, totalAdvance };
  };

  const resetOrderFlow = () => {
    setOrderFlowPhase(null);
    setCompletedOrderResult(null);
    setCompletedInquiryResult(null);
    setShowCheckoutModal(false);
  };

  const handleOpenInquiryWhatsApp = () => {
    if (!completedInquiryResult) return;
    openShopInquiryWhatsAppFromCart({
      inquiry: completedInquiryResult.inquiry,
      cart: completedInquiryResult.cart,
      customerName: completedInquiryResult.customerName,
      phone: completedInquiryResult.phone,
      deliveryDate: completedInquiryResult.deliveryDate,
      itemName,
      t,
    });
  };

  const runOrderCompletion = async (response, activeCart) => {
    try {
      const result = await completePublicGalleryOrderAfterPayment({
        response,
        activeCart,
        locale,
        orderType: shopOrderType,
        verifyPayment,
        createPublicOrder,
        toast,
        t,
        onPhaseChange: setOrderFlowPhase,
      });

      if (result) {
        setCompletedOrderResult(result);
        setCart([]);
        clearShopCart();
        setShowCheckoutModal(false);
      }
    } catch (error) {
      console.error('Order completion error:', error);
      setOrderFlowPhase(null);
      setCompletedOrderResult(null);
      toast.error(error.message || t('toast.orderAfterPaymentFail'));
      throw error;
    }
  };

  const handleSimulateTestPayment = async () => {
    const prepared = prepareCheckoutCart();
    if (!prepared) return;

    const { updatedCart, totalAdvance } = prepared;

    try {
      setSimulatingPayment(true);
      setCart(updatedCart);

      const paymentOrder = await createPaymentOrder({
        amount: totalAdvance,
        currency: 'INR',
        receipt: `receipt_${Date.now()}`,
        notes: {
          customerName: updatedCart[0].customerName,
          phone: updatedCart[0].phone,
          itemCount: updatedCart.length,
          paymentType: 'advance',
        },
      });

      const simulated = await simulateTestPayment({ razorpay_order_id: paymentOrder.orderId });

      await runOrderCompletion(simulated, updatedCart);
    } catch (error) {
      console.error('Simulated payment error:', error);
      toast.error(error.message || t('toast.orderAfterPaymentFail'));
    } finally {
      setSimulatingPayment(false);
    }
  };

  const handleRemoveFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearCart = () => {
    if (cart.length === 0) return;
    if (!window.confirm(t('cart.clearCartConfirm'))) return;
    setCart([]);
    clearShopCart();
    toast.success(t('toast.cartCleared'));
  };

  const handlePayment = async (advanceAmountOverride = null, cartForCheckout = null) => {
    const activeCart = cartForCheckout ?? cart;

    if (activeCart.length === 0) {
      toast.error(t('toast.cartEmpty'));
      return;
    }

    try {
      // Use provided advance amount or calculate from cart (customers pay advance, balance collected later)
      const advanceAmount = roundRupees(
        advanceAmountOverride !== null ? advanceAmountOverride : calculateTotalAdvance()
      );

      // Validate that advance amount is greater than 0
      if (advanceAmount <= 0) {
        toast.error(t('toast.advancePositive'));
        return;
      }

      // Step 1: Create Razorpay payment order
      const paymentOrder = await createPaymentOrder({
        amount: advanceAmount,
        currency: 'INR',
        receipt: `receipt_${Date.now()}`,
        notes: {
          customerName: activeCart[0].customerName,
          phone: activeCart[0].phone,
          itemCount: activeCart.length,
          paymentType: 'advance'
        }
      });

      console.log('Payment order created:', paymentOrder);

      // Step 2: Configure Razorpay checkout options
      const options = buildRazorpayCheckoutOptions({
        key: paymentOrder.key, // Razorpay key from backend
        amount: paymentOrder.amount, // Amount in paisa
        currency: paymentOrder.currency,
        order_id: paymentOrder.orderId,
        name: t('razorpay.orgName'),
        description:
          activeCart.length === 1
            ? t('razorpay.orderDescription', { count: activeCart.length })
            : t('razorpay.orderDescriptionPlural', { count: activeCart.length }),
        image: '/logo.png',

        // Customer details
        prefill: {
          name: activeCart[0].customerName,
          contact: activeCart[0].phone,
          email: '' // Optional
        },

        // Theme
        theme: {
          color: '#ea580c',
        },

        // Success handler
        handler: async function (response) {
          console.log('Payment successful:', response);

          try {
            await runOrderCompletion(response, activeCart);
          } catch (orderError) {
            console.error('Error creating order after payment:', orderError);
          }
        },

        // Payment failure handler
        modal: {
          ondismiss: function() {
            toast.error(t('toast.paymentCancelled'));
          }
        }
      }, { testMode: paymentOrder.testMode });

      // Step 5: Open Razorpay checkout
      const rzp = new window.Razorpay(options);

      // Handle payment failure
      rzp.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        toast.error(t('toast.paymentFailed'));
      });

      // Open payment modal
      rzp.open();

    } catch (error) {
      console.error('Payment initialization error:', error);
      toast.error(error.message || t('toast.paymentInitFailed'));
    }
  };

  return (
    <>
      <style>{`
        @keyframes codepenGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes omFloat {
          0% { transform: translate3d(0, 0, 0) rotate(0deg); }
          50% { transform: translate3d(var(--om-drift-x), -18px, 0) rotate(3deg); }
          100% { transform: translate3d(calc(var(--om-drift-x) * -0.35), -34px, 0) rotate(-2deg); }
        }
        @keyframes omFade {
          0%   { opacity: 0; }
          15%  { opacity: 0.06; }
          45%  { opacity: 0.28; }
          80%  { opacity: 0.06; }
          100% { opacity: 0; }
        }
        @keyframes loaderRingA {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes loaderRingB {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
        @keyframes loaderOmPulse {
          0%, 100% { opacity: 0.88; transform: scale(1);   filter: brightness(10) saturate(0) drop-shadow(0 0 12px rgba(255,255,255,0.4)); }
          50%      { opacity: 1;    transform: scale(1.1); filter: brightness(10) saturate(0) drop-shadow(0 0 28px rgba(255,255,255,0.75)); }
        }
        @keyframes loaderDotBounce {
          0%, 80%, 100% { transform: translateY(0);   opacity: 0.5; }
          40%           { transform: translateY(-8px); opacity: 1; }
        }
      `}</style>
      <ShopToaster />
      <ShopOrderFlowOverlay
        phase={orderFlowPhase}
        orderResult={completedOrderResult}
        inquiryResult={completedInquiryResult}
        onContinue={resetOrderFlow}
        onOpenWhatsApp={
          completedInquiryResult ? handleOpenInquiryWhatsApp : undefined
        }
        onDownloadPdf={
          completedOrderResult?.order?._id
            ? () => downloadShopOrderReceipt(completedOrderResult, 'pdf')
            : undefined
        }
        t={t}
      />

      {/* Full-page traditional loader — shown only on initial page load */}
      <AnimatePresence>
        {pageLoading && (
          <motion.div
            className="fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden"
            style={{ background: activeThemeObj.gradient }}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.04 }}
            transition={{ duration: 0.9, ease: 'easeInOut' }}
          >
            {/* Floating Om particles in background — skipped when reduceMotion is on */}
            {!reduceMotion && (
              <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
                {omParticles.slice(0, 12).map((p) => (
                  <span
                    key={`loader-${p.id}`}
                    className="absolute select-none"
                    style={{
                      top: `${p.top}%`,
                      left: `${p.left}%`,
                      animation: `omFloat ${p.duration}s ease-in-out ${p.delay}s infinite, omFade ${4 + p.duration * 0.13}s linear ${p.delay * 0.6}s infinite`,
                      '--om-drift-x': `${p.driftX}px`,
                      willChange: 'transform, opacity',
                    }}
                  >
                    <img
                      src={omSymbolSvgUrl}
                      alt=""
                      aria-hidden="true"
                      className="block object-contain"
                      style={{
                        width: `${p.size}px`,
                        height: `${p.size}px`,
                        opacity: 0.9,
                        filter: 'brightness(10) saturate(0)',
                      }}
                    />
                  </span>
                ))}
              </div>
            )}

            {/* Mandala rings + Om center */}
            <div
              className="relative flex items-center justify-center"
              style={{ width: 'min(220px, 56vmin)', height: 'min(220px, 56vmin)' }}
            >
              {/* Outer ring — clockwise dashed */}
              <div
                className="absolute rounded-full border-2 border-dashed border-white/30"
                style={{ width: 'min(220px, 56vmin)', height: 'min(220px, 56vmin)', animation: reduceMotion ? 'none' : 'loaderRingA 12s linear infinite' }}
              />
              {/* Mid ring — counter-clockwise dotted */}
              <div
                className="absolute rounded-full border-2 border-dotted border-white/45"
                style={{ width: 'min(160px, 41vmin)', height: 'min(160px, 41vmin)', animation: reduceMotion ? 'none' : 'loaderRingB 8s linear infinite' }}
              />
              {/* Inner ring — clockwise solid */}
              <div
                className="absolute rounded-full border border-white/60"
                style={{ width: 'min(110px, 28vmin)', height: 'min(110px, 28vmin)', animation: reduceMotion ? 'none' : 'loaderRingA 5s linear infinite' }}
              />
              {/* Om symbol — pulsing glow */}
              <img
                src={omSymbolSvgUrl}
                alt="ॐ"
                style={{
                  width: 'min(72px, 18vmin)',
                  height: 'min(72px, 18vmin)',
                  animation: reduceMotion ? 'none' : 'loaderOmPulse 2.4s ease-in-out infinite',
                  filter: 'brightness(10) saturate(0)',
                  opacity: reduceMotion ? 1 : undefined,
                }}
              />
            </div>

            {/* Devotional text */}
            <p className="mt-6 text-center px-4 text-white/90 text-lg sm:text-xl font-semibold tracking-wide sm:tracking-widest drop-shadow">
              श्री गणेशाय नमः
            </p>
            <p className="mt-1 text-center px-4 text-white/65 text-sm tracking-wider">
              {t('header.titleShort')}
            </p>

            {/* Bouncing dots */}
            <div className="mt-5 flex gap-2">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="block h-2 w-2 rounded-full bg-white/70"
                  style={{ animation: reduceMotion ? 'none' : `loaderDotBounce 1.2s ease-in-out ${i * 0.2}s infinite` }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        ref={shopRootRef}
        className="relative flex min-h-full min-h-screen flex-col overflow-x-clip"
        style={{ background: activeThemeObj.gradientSubtle, '--shop-header-offset': '5rem' }}
      >
        {!reduceMotion && (
          <>
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background: 'linear-gradient(-45deg, #fffaf2, #f7efe4, #fffdf8, #f4ece1)',
                backgroundSize: '400% 400%',
                animation: 'codepenGradient 21s ease infinite',
                willChange: 'background-position',
              }}
            />
            <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
              {omParticles.map((particle) => (
                <span
                  key={particle.id}
                  className="absolute select-none"
                  style={{
                    top: `${particle.top}%`,
                    left: `${particle.left}%`,
                    animation: `omFloat ${particle.duration}s ease-in-out ${particle.delay}s infinite, omFade ${4 + particle.duration * 0.13}s linear ${particle.delay * 0.6}s infinite`,
                    '--om-drift-x': `${particle.driftX}px`,
                    willChange: 'transform, opacity',
                  }}
                >
                  <img
                    src={omSymbolSvgUrl}
                    alt=""
                    aria-hidden="true"
                    className="block object-contain"
                    style={{
                      width: `${particle.size}px`,
                      height: `${particle.size}px`,
                      opacity: 0.9,
                      filter: activeThemeObj.symbolFilter,
                    }}
                  />
                </span>
              ))}
            </div>
          </>
        )}

        <div className="relative z-10 flex flex-1 flex-col">
          <header
            ref={headerRef}
            className={cn(
              'fixed top-0 left-0 right-0 z-50 overflow-hidden py-2 sm:py-4 transition-all duration-500',
              scrolled
                ? 'border-b border-border/80 bg-card/95 backdrop-blur-xl shadow-sm'
                : 'border-b border-transparent bg-transparent'
            )}
          >
            {!reduceMotion && scrolled && (
              <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                {omParticles.slice(0, 8).map((particle) => (
                  <span
                    key={`header-${particle.id}`}
                    className="absolute select-none"
                    style={{
                      top: `${particle.top}%`,
                      left: `${particle.left}%`,
                      animation: `omFloat ${particle.duration + 4}s ease-in-out ${particle.delay}s infinite, omFade ${5 + particle.duration * 0.13}s linear ${particle.delay * 0.55}s infinite`,
                      '--om-drift-x': `${particle.driftX * 0.4}px`,
                      willChange: 'transform, opacity',
                    }}
                  >
                    <img
                      src={omSymbolSvgUrl}
                      alt=""
                      aria-hidden="true"
                      className="block object-contain"
                      style={{
                        width: `${Math.max(14, particle.size - 2)}px`,
                        height: `${Math.max(14, particle.size - 2)}px`,
                        opacity: 0.55,
                        filter: activeThemeObj.symbolFilter,
                      }}
                    />
                  </span>
                ))}
              </div>
            )}
            <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                  <img
                    src="/logo.png"
                    alt={t('header.logoAlt')}
                    className="h-9 w-9 shrink-0 drop-shadow-sm sm:h-12 sm:w-12"
                  />
                  <div className="min-w-0 text-left">
                    <h1
                      className={cn(
                        'font-heading mb-1 text-lg font-semibold tracking-tight transition-colors duration-500 sm:text-2xl',
                        scrolled ? 'text-primary' : 'text-white drop-shadow-md'
                      )}
                    >
                      {t('header.titleShort')}
                    </h1>
                    <p
                      className={cn(
                        'mt-0.5 text-xs font-light leading-relaxed transition-colors duration-500 sm:mt-1 sm:text-sm',
                        scrolled ? 'text-muted-foreground' : 'text-white/80'
                      )}
                    >
                      {t('header.slogan')}
                    </p>
                  </div>
                </div>
                <nav className="flex shrink-0 items-center pr-4 sm:pr-6" aria-label={t('lang.toggleAria')}>
                  <div
                    role="group"
                    aria-label={t('lang.toggleAria')}
                    className={cn('inline-flex items-center self-center rounded-xl border p-0.5 shadow-sm transition-colors duration-500', scrolled ? 'border-border/80 bg-muted/60' : 'border-white/30 bg-black/20 backdrop-blur-sm')}
                  >
                    <button
                      type="button"
                      onClick={() => setLocale('en')}
                      aria-pressed={locale === 'en'}
                      className={cn(
                        'rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors',
                        locale === 'en'
                          ? 'bg-primary text-primary-foreground'
                          : scrolled ? 'text-muted-foreground hover:bg-muted' : 'text-white/80 hover:bg-white/20'
                      )}
                    >
                      {t('lang.enShort')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLocale('mr')}
                      aria-pressed={locale === 'mr'}
                      className={cn(
                        'rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors',
                        locale === 'mr'
                          ? 'bg-primary text-primary-foreground'
                          : scrolled ? 'text-muted-foreground hover:bg-muted' : 'text-white/80 hover:bg-white/20'
                      )}
                    >
                      {t('lang.mrShort')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLocale('kn')}
                      aria-pressed={locale === 'kn'}
                      className={cn(
                        'rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors',
                        locale === 'kn'
                          ? 'bg-primary text-primary-foreground'
                          : scrolled ? 'text-muted-foreground hover:bg-muted' : 'text-white/80 hover:bg-white/20'
                      )}
                    >
                      {t('lang.knShort')}
                    </button>
                  </div>
                </nav>
              </div>
            </div>
          </header>

          {/* Full-page hero collage */}
          <div className="relative h-screen overflow-hidden">
            {HERO_SLIDES.map((slide, si) => (
              <div
                key={si}
                className={cn(
                  'absolute inset-0 transition-opacity duration-1000',
                  heroSlide === si ? 'opacity-100' : 'opacity-0'
                )}
              >
                <div className="flex h-full gap-0.5">
                  {slide.map((src, i) => (
                    <div key={i} className={cn('relative flex-1 overflow-hidden', i >= 2 && 'hidden sm:block')}>
                      <img src={src} alt="" className="h-full w-full object-cover object-top" />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Top gradient for header readability */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-black/70 to-transparent" />
            {/* Bottom dark vignette — dissolves fully before the midpoint */}
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-96"
              style={{
                background: `linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.40) 40%, transparent 100%)`,
              }}
            />
            {/* Bottom theme tint — starts transparent, stays transparent above 45% */}
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-96"
              style={{
                background: `linear-gradient(to top, rgba(${activeThemeObj.gradientRgb}, 0.50) 0%, rgba(${activeThemeObj.gradientRgb}, 0.15) 45%, transparent 100%)`,
              }}
            />

            {/* CTA overlay */}
            <div className="absolute inset-x-0 bottom-0 flex w-full flex-col items-center pb-16 sm:pb-20 px-4 text-center">
              <h2 className="font-heading mb-2 text-2xl font-bold text-white drop-shadow-lg sm:hidden">
                {t('header.titleShort')}
              </h2>
              <h2 className="font-heading mb-2 hidden text-5xl font-bold text-white drop-shadow-lg sm:block">
                {t('header.title')}
              </h2>
              <p className="mb-5 max-w-xs text-xs text-white/80 drop-shadow sm:mb-8 sm:max-w-none sm:text-base">
                {t('header.subtitle')}
              </p>
              <button
                onClick={scrollToGallery}
                className="rounded-full bg-white/90 px-8 py-3 text-sm font-semibold text-foreground shadow-lg backdrop-blur-sm transition hover:bg-white hover:scale-105"
              >
                {t('bookCta.bookIdol')} ↓
              </button>
            </div>

            {/* Slide dots */}
            <div className="absolute bottom-4 sm:bottom-8 left-1/2 flex -translate-x-1/2 gap-2">
              {HERO_SLIDES.map((_, si) => (
                <button
                  key={si}
                  onClick={() => setHeroSlide(si)}
                  aria-label={`Slide ${si + 1}`}
                  className={cn(
                    'h-1.5 rounded-full transition-all duration-300',
                    heroSlide === si ? 'w-6 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'
                  )}
                />
              ))}
            </div>
          </div>

          <section
            className={`scroll-mt-20 py-4 sm:py-5${cart.length > 0 ? ' pb-24 sm:pb-28' : ''}`}
          >
            <div className="mx-auto max-w-6xl px-4 sm:px-6">
            {/* Collage section — temporarily hidden
            <div className="mb-10">
              <div className="mb-4">
                <h3 className="font-heading text-primary text-xl font-semibold sm:text-2xl">{t('collage.heading')}</h3>
                <p className="text-muted-foreground mt-1 text-sm">
                  {t('collage.subheading')}
                </p>
              </div>

              <div className="rounded-xl border border-primary/15 bg-card p-3 shadow-sm sm:p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:hidden">
                  {collagePlaceholderImages.map((image) => (
                    <div key={`mobile-${image.id}`} className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                      <img
                        src={collageImageErrors[image.id] ? image.fallbackSrc : image.src}
                        alt={image.alt}
                        onError={() => handleCollageImageError(image.id)}
                        className="h-32 w-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent p-2.5">
                        <p className="text-xs font-medium text-white">{t(collageLabelKey(image.id))}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4"
                  onMouseLeave={() => setActiveCollageId(null)}
                >
                  {collagePlaceholderImages.map((image) => {
                    const isActive = activeCollageId === image.id;
                    const hasHovered = activeCollageId !== null;
                    const isDimmed = hasHovered && !isActive;

                    return (
                      <div
                        key={`desktop-${image.id}`}
                        onMouseEnter={() => setActiveCollageId(image.id)}
                        className="group relative h-[200px] overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
                      >
                        <img
                          src={collageImageErrors[image.id] ? image.fallbackSrc : image.src}
                          alt={image.alt}
                          onError={() => handleCollageImageError(image.id)}
                          loading="lazy"
                          className={`h-full w-full object-cover transition-transform duration-500 ${
                            isActive
                              ? 'scale-110 blur-0 brightness-105 will-change-transform'
                              : isDimmed
                                ? 'scale-100 blur-[1.8px] brightness-75'
                                : 'scale-100 blur-0 brightness-95'
                          }`}
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent p-4">
                          <p className="text-xs font-medium tracking-wide text-accent-foreground/85">{t('collage.watermark')}</p>
                          <p className="text-sm font-semibold text-white">{t(collageLabelKey(image.id))}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            */}

          <div ref={sizeBarSentinelRef} className="h-px" aria-hidden="true" />

          {/* Wholesale entry banner — visible when scrolled to models; this is now the target for "Book Your Idol" */}
          <div ref={galleryRef} className="mx-auto max-w-6xl px-4 sm:px-6 mb-3 scroll-mt-16 sm:scroll-mt-20">
            <div className="rounded-xl border border-violet-100 bg-violet-50/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-foreground">{t('filters.wholesaleBannerTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('filters.wholesaleBannerHint')}</p>
              </div>
              <Link
                to="/shop/wholesale"
                className="inline-flex h-8 sm:h-9 w-full sm:w-auto items-center justify-center rounded-lg border border-violet-300 bg-violet-100 px-4 text-sm font-semibold text-violet-800 whitespace-nowrap hover:bg-violet-200 transition-colors"
              >
                {t('nav.wholesaleOrder')}
              </Link>
            </div>

            {catalogUpdating && (
              <div
                className="mt-3 flex gap-3 rounded-xl border-2 border-amber-400 border-l-4 border-l-amber-600 bg-amber-100 px-5 py-4 shadow-sm sm:px-6 sm:py-5"
                role="status"
              >
                <span
                  className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 text-base font-bold text-white sm:h-10 sm:w-10 sm:text-lg"
                  aria-hidden="true"
                >
                  ✦
                </span>
                <div className="min-w-0">
                  <p className="text-base font-bold text-amber-950 sm:text-lg">
                    {t('gallery.catalogUpdatingTitle')}
                  </p>
                  <p className="mt-1 text-sm text-amber-900">
                    {t('gallery.catalogUpdatingHint')}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="galleryScrollContext">
            {/* Sticky horizontal filter toolbar — spans full gallery scroll height */}
            <div
              id="gallery"
              className={cn(
                'sticky z-30 rounded-xl border border-border bg-card/95 shadow-md backdrop-blur-xl',
                'p-1.5 sm:p-2 mb-3',
                sizeBarStuck && 'shadow-lg ring-1 ring-border/40'
              )}
              style={{ top: 'var(--shop-header-offset, 5rem)' }}
            >
              <div className="shop-filter-toolbar">
                <span className="shop-filter-label shrink-0 text-[10px] sm:text-xs font-semibold text-foreground whitespace-nowrap sm:hidden">
                  {t('filters.chooseSizeShort')}
                </span>
                <span className="shop-filter-label hidden shrink-0 text-[10px] sm:text-xs font-semibold text-foreground whitespace-nowrap sm:inline">
                  {t('filters.chooseSize')}
                </span>

                <div className="shop-filter-pills filter-scroll filter-scroll--compact min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => handleCategorySelect(null)}
                    className={sizePillClass(selectedCategory === null)}
                  >
                    <span>{t('filters.all')}</span>
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleCategorySelect(category.id)}
                      className={sizePillClass(selectedCategory === category.id)}
                    >
                      <span>{categoryLabel(category.id, !isSmUp)}</span>
                    </button>
                  ))}
                </div>

                <div className="shop-filter-actions">
                  <label className="flex h-7 sm:h-8 items-center gap-1 text-[11px] sm:text-xs text-muted-foreground">
                    <span className="hidden sm:inline whitespace-nowrap">{t('filters.sort')}</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="toolbar-select h-7 sm:h-8 min-w-[3.5rem] max-w-[5rem] sm:min-w-[5.5rem] sm:max-w-none rounded-lg border-input bg-card text-[11px] sm:text-xs text-foreground focus-visible:ring-ring"
                    >
                      <option value="model">
                        {t(isSmUp ? 'filters.sortModelNumber' : 'filters.sortModelNumberShort')}
                      </option>
                      <option value="price-low">
                        {t(isSmUp ? 'filters.sortPriceLow' : 'filters.sortPriceLowShort')}
                      </option>
                      <option value="price-high">
                        {t(isSmUp ? 'filters.sortPriceHigh' : 'filters.sortPriceHighShort')}
                      </option>
                    </select>
                  </label>

                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className={`shrink-0 h-7 sm:h-8 px-1.5 sm:px-2 text-[11px] sm:text-xs whitespace-nowrap rounded-lg ${uiTokens.buttonSecondary}`}
                  >
                    <span className="sm:hidden">{t('filters.clearFiltersShort')}</span>
                    <span className="hidden sm:inline">{t('filters.clearFilters')}</span>
                  </button>

                  <span className="hidden sm:inline shrink-0 text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap">
                    {t('filters.modelsCount', { count: allInventory.length })}
                  </span>
                </div>
              </div>
            </div>

          {/* Cart Section - after filter bar */}
          {cart.length > 0 && (
            <div className="mx-0 mb-4 mt-4 rounded-xl border border-primary/15 bg-card/80 shadow-elegant backdrop-blur-sm">
              <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
                {/* Mobile: Compact Cart Summary */}
                <div className="block lg:hidden">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-secondary p-2 rounded-lg">
                        <span className="text-2xl">🛒</span>
                      </div>
                      <div>
                        <p className="font-semibold">
                          {t(cart.length === 1 ? 'cart.itemsInCart_one' : 'cart.itemsInCart_other', {
                            count: cart.length,
                          })}
                        </p>
                        <p className="text-sm text-muted-foreground">{t('cart.total')} ₹{calculateGrandTotal().toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setCartExpanded(!cartExpanded)}
                      className="text-primary font-medium flex items-center gap-1"
                    >
                      {cartExpanded ? t('cart.collapse') : t('cart.viewCart')}
                      <span className={`transform transition-transform ${cartExpanded ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </button>
                  </div>

                  {/* Mobile Checkout Button */}
                  <div className="pt-3 border-t border-border flex flex-col gap-2">
                    <button
                      onClick={handleCheckout}
                      className="w-full rounded-lg bg-accent px-6 py-3 font-bold text-accent-foreground transition-colors hover:bg-accent/90"
                    >
                      {shopPaymentEnabled === true
                        ? t('cart.proceedCheckout')
                        : t('cart.proceedInquiry')}
                    </button>
                    <button
                      onClick={handleClearCart}
                      className={`w-full px-4 py-2 text-xs ${uiTokens.buttonSecondary}`}
                    >
                      {t('cart.clearCart')}
                    </button>
                  </div>

                  {/* Mobile: Expanded Cart Details */}
                  {cartExpanded && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      {cart.map((item, index) => (
                        <div key={index} className="bg-muted p-3 rounded-lg flex items-center gap-3">
                          <img src={item.photo} alt={itemName(item)} className="w-10 h-10 object-cover rounded" />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{itemName(item)}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('cart.qtyShort')} {item.quantity}
                              <> × ₹{roundRupees(cartLineUnitPrice(item)).toLocaleString('en-IN')}</>
                            </p>
                          </div>
                          <button
                            onClick={() => handleRemoveFromCart(index)}
                            className="text-muted-foreground hover:text-foreground text-lg flex-shrink-0"
                            title={t('cart.removeItemTitle')}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Desktop: Full Cart Layout */}
                <div className="hidden lg:block">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div className="flex-1 flex items-center gap-4">
                      <h3 className="font-bold text-lg">
                        {t(cart.length === 1 ? 'cart.yourOrder_one' : 'cart.yourOrder_other', { count: cart.length })}
                      </h3>
                    </div>

                    {/* Desktop Checkout Button */}
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={handleCheckout}
                        className="rounded-lg bg-accent px-6 py-3 font-bold text-accent-foreground transition-colors hover:bg-accent/90"
                      >
                        {shopPaymentEnabled === true
                        ? t('cart.proceedCheckout')
                        : t('cart.proceedInquiry')}
                      </button>
                      <button
                        onClick={handleClearCart}
                        className={`px-4 py-2 text-sm ${uiTokens.buttonSecondary}`}
                      >
                        {t('cart.clearCart')}
                      </button>
                    </div>
                  </div>

                  {/* Cart Items */}
                  <div className="flex flex-wrap gap-4 mt-4">
                    {cart.map((item, index) => (
                      <div key={index} className="bg-muted p-3 rounded-lg flex items-center gap-3">
                        <img src={item.photo} alt={itemName(item)} className="w-12 h-12 object-cover rounded" />
                        <div className="flex-1">
                          <p className="font-semibold">{itemName(item)}</p>
                          <p className="text-sm text-muted-foreground">
                            {t('cart.qtyShort')} {item.quantity}
                            <> × ₹{roundRupees(cartLineUnitPrice(item)).toLocaleString('en-IN')}</>
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveFromCart(index)}
                          className="text-muted-foreground hover:text-foreground text-xl flex-shrink-0"
                          title={t('cart.removeItemTitle')}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            )}

          <motion.div
            variants={sectionMotion}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {loading ? (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((idx) => (
                  <div key={idx} className="flex flex-col h-full rounded-2xl border border-border bg-card shadow-sm animate-pulse overflow-hidden">
                    <div className="aspect-[3/4] bg-muted" />
                    <div className="p-2.5 space-y-1.5">
                      <div className="h-2 bg-muted rounded w-1/3" />
                      <div className="h-3.5 bg-muted rounded w-2/3" />
                      <div className="flex items-center justify-between gap-2 pt-0.5">
                        <div className="h-4 bg-muted rounded w-1/3" />
                        <div className="h-6 w-16 bg-muted rounded-full" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {displayItems.length === 0 ? (
                  catalogEmpty ? (
                    <div className="mt-4 rounded-2xl border border-dashed border-input bg-card py-16 text-center px-4">
                      <h3 className="text-2xl font-semibold text-foreground mb-2">{t('gallery.catalogEmptyTitle')}</h3>
                      <p className="text-muted-foreground mb-3 max-w-lg mx-auto">{t('gallery.catalogEmptyHint')}</p>
                      <p className="text-sm text-muted-foreground/80">{t('gallery.catalogEmptyThanks')}</p>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl border border-dashed border-input bg-card py-16 text-center">
                      <div className="text-5xl mb-4">{t('gallery.noMatchEmoji')}</div>
                      <h3 className="text-2xl font-semibold text-foreground mb-2">{t('gallery.noIdolsTitle')}</h3>
                      <p className="text-muted-foreground mb-4">{t('gallery.noIdolsHint')}</p>
                      <button
                        onClick={clearAllFilters}
                        className={`px-6 py-2.5 ${uiTokens.buttonPrimary}`}
                      >
                        {t('gallery.clearAllFilters')}
                      </button>
                    </div>
                  )
                ) : (
                  <div
                    className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5"
                    onMouseLeave={() => { setActiveCardId(null); setZoomOrigin({ x: 50, y: 50 }); }}
                  >
                    {displayItems.map(item => {
                      const outOfStock = isShopItemOutOfStock(item);
                      return (
                      <div
                        key={item.id}
                        onMouseEnter={() => setActiveCardId(item.id)}
                        className={cn(
                          'flex flex-col h-full cursor-pointer overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-300',
                          outOfStock && 'opacity-90',
                          activeCardId !== null && activeCardId !== item.id
                            ? 'blur-[2px] opacity-50 scale-[0.98]'
                            : !outOfStock && 'opacity-100 scale-100'
                        )}
                      >
                        <div
                          className="relative aspect-[3/4] bg-muted/30 flex items-center justify-center overflow-hidden"
                          onClick={() => handleItemClick(item)}
                          onMouseMove={activeCardId === item.id ? handleImageMouseMove : undefined}
                        >
                          <img
                            src={item.photo}
                            alt={itemName(item)}
                            className={cn(
                              'max-h-full max-w-full object-contain block transition-transform duration-300',
                              outOfStock && 'grayscale opacity-75'
                            )}
                            style={
                              activeCardId === item.id && !outOfStock
                                ? { transform: 'scale(1.8)', transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%` }
                                : { transform: 'scale(1)', transformOrigin: 'center center' }
                            }
                            loading="lazy"
                          />
                          {outOfStock && (
                            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/20">
                              <span className="rounded-full border border-border bg-card/95 px-2.5 py-1 text-[10px] font-semibold text-muted-foreground shadow-sm">
                                {t('gallery.stockOutOfStock')}
                              </span>
                            </div>
                          )}
                          {renderGalleryBadges(item)}
                        </div>
                        {renderGalleryFooter(item)}
                      </div>
                    );
                    })}
                  </div>
                )}

                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                    <h4 className="font-semibold text-foreground mb-1">
                      {shopPaymentEnabled === true
                        ? t('trustRow.secureTitle')
                        : t('trustRow.inquiryTitle')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {shopPaymentEnabled === true
                        ? t('trustRow.secureBody')
                        : t('trustRow.inquiryBody')}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                    <h4 className="font-semibold text-foreground mb-1">{t('trustRow.deliveryTitle')}</h4>
                    <p className="text-sm text-muted-foreground">{t('trustRow.deliveryBody')}</p>
                  </div>
                  <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                    <h4 className="font-semibold text-foreground mb-1">{t('trustRow.craftTitle')}</h4>
                    <p className="text-sm text-muted-foreground">{t('trustRow.craftBody')}</p>
                  </div>
                </div>
              </>
            )}
          </motion.div>
          </div>
          </div>
          </section>

        </div>
        {/* Footer */}
        <footer
          className="relative z-10 mt-16 overflow-hidden border-t border-primary-foreground/20 py-14 text-primary-foreground shadow-[0_-12px_36px_rgba(0,0,0,0.12)]"
          style={{ background: activeThemeObj.gradient }}
        >
          {!reduceMotion && (
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            {omParticles.slice(8, 20).map((particle) => (
              <span
                key={`footer-${particle.id}`}
                className="absolute select-none"
                style={{
                  top: `${particle.top}%`,
                  left: `${particle.left}%`,
                  animation: `omFloat ${particle.duration + 5}s ease-in-out ${particle.delay}s infinite, omFade ${5 + particle.duration * 0.13}s linear ${particle.delay * 0.5}s infinite`,
                  '--om-drift-x': `${particle.driftX * 0.35}px`,
                  willChange: 'transform, opacity',
                }}
              >
                <img
                  src={omSymbolSvgUrl}
                  alt=""
                  aria-hidden="true"
                  className="block object-contain"
                  style={{
                    width: `${Math.max(15, particle.size)}px`,
                    height: `${Math.max(15, particle.size)}px`,
                    opacity: 0.72,
                    filter: activeThemeObj.symbolFilter,
                  }}
                />
              </span>
            ))}
          </div>
          )}
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Company Info */}
              <div>
                <div className="flex items-center mb-4">
                  <img
                    src="/logo.png"
                    alt={t('header.logoAlt')}
                    className="w-10 h-10 mr-3"
                  />
                  <h3 className="text-xl font-bold">{t('footer.companyTitle')}</h3>
                </div>
                <p className="text-primary-foreground/90 mb-4">
                  {t('footer.companyLead')}
                </p>
                <p className="text-sm text-primary-foreground/80">
                  {t('footer.companyTagline')}
                </p>
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="text-lg font-semibold mb-4">{t('footer.contactTitle')}</h4>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <svg className="h-5 w-5 shrink-0 text-primary-foreground/90" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="leading-normal text-primary-foreground/90">{t('footer.address')}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <svg className="h-5 w-5 shrink-0 text-primary-foreground/90" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <span className="leading-normal text-primary-foreground/90">+91-9972454799</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <svg className="h-5 w-5 shrink-0 text-primary-foreground/90" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <span className="leading-normal text-primary-foreground/90">vinayaksimu@gmail.com</span>
                  </li>
                </ul>
              </div>

              {/* Quick Links & Info */}
              <div>
                <h4 className="text-lg font-semibold mb-4">{t('footer.infoTitle')}</h4>
                <div className="space-y-2">
                  <a
                    href="/admin/login"
                    className="mt-1 block text-sm text-primary-foreground/90 underline underline-offset-2 hover:text-primary-foreground"
                  >
                    {t('footer.adminLogin')}
                  </a>
                  <p className="text-primary-foreground/90 text-sm">
                    <span className="font-medium">{t('footer.hoursLabel')}</span>
                    <span className="mt-1 block whitespace-pre-line">{t('footer.hoursBody')}</span>
                  </p>
                  <p className="text-primary-foreground/90 text-sm">
                    <span className="font-medium">{t('footer.paymentLabel')}</span><br />
                    {t('footer.paymentBody')}
                  </p>
                  <p className="text-primary-foreground/90 text-sm">
                    <span className="font-medium">{t('footer.deliveryLabel')}</span><br />
                    {t('footer.deliveryBody')}
                  </p>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="border-t border-primary-foreground/25 mt-8 pt-8 text-center">
              <p className="text-primary-foreground/85 text-sm">
                {t('footer.copyright', { year: new Date().getFullYear() })}
                {' '}|{' '}
                <span className="text-primary-foreground">{t('footer.craftedWith')}</span>
              </p>
              <p className="text-primary-foreground/85 text-xs mt-2">
                {t('footer.systemTag')}
              </p>

              {/* Theme switcher */}
              <div className="mt-6 flex items-center justify-center gap-3 py-5 pb-6">
                <span className="text-xs text-primary-foreground/60 font-medium uppercase tracking-widest">
                  Theme
                </span>
                <div className="flex items-center gap-2">
                  {THEMES.map(theme => (
                    <button
                      key={theme.id}
                      onClick={() => setActiveTheme(theme.id)}
                      title={theme.name}
                      style={{ background: theme.gradient }}
                      className={cn(
                        'h-7 w-7 rounded-full border-2 transition-all duration-200',
                        activeTheme === theme.id
                          ? 'border-white scale-105 shadow-lg ring-2 ring-white/40'
                          : 'border-transparent opacity-70 hover:opacity-100 hover:scale-105'
                      )}
                      aria-pressed={activeTheme === theme.id}
                      aria-label={theme.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Order Modal — bottom sheet on mobile, centered dialog on desktop */}
      {showModal && selectedItem && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />

          {/* Sheet / dialog */}
          <div className={cn(
            'fixed z-50 bg-card text-card-foreground shadow-2xl overflow-y-auto',
            // Mobile: bottom sheet
            'bottom-0 left-0 right-0 max-h-[95dvh] rounded-t-2xl',
            // Desktop: centered dialog
            'sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2',
            'sm:w-full sm:max-w-lg sm:max-h-[95vh] sm:rounded-2xl sm:border sm:border-border'
          )}>
            {/* Drag handle — mobile only */}
            <div className="mx-auto mt-3 mb-1 h-1 w-10 rounded-full bg-muted-foreground/30 sm:hidden" />

            <div className="p-3 sm:p-4">
              {/* Header row */}
              <div className="flex items-start justify-between mb-3">
                <h2 className="text-base sm:text-xl font-bold text-foreground pr-2 leading-tight">
                  {itemName(selectedItem)}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-shrink-0 text-muted-foreground hover:text-foreground text-xl p-1 hover:bg-muted rounded"
                >
                  ✕
                </button>
              </div>

              {/* Image + details — compact thumbnail row */}
              <div className="mb-3 flex items-start gap-2">
                <div className="w-[88px] shrink-0 sm:w-[100px]">
                  <div className="aspect-square overflow-hidden rounded-lg shadow-md">
                    <img
                      src={selectedItem.photo}
                      alt={itemName(selectedItem)}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
                <div className="min-w-0 flex-1 flex flex-col justify-center gap-1">
                  <p className="text-xs text-muted-foreground">{t('gallery.modelPrefix')} {formatModelCode(itemSize(selectedItem), selectedItem.modelNumber)}</p>
                  {selectedItem.retailOnly && (
                    <span className="inline-flex w-fit px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
                      {t('gallery.retailOnlyBadge')}
                    </span>
                  )}
                  <p className="text-lg sm:text-xl font-bold text-primary">
                    ₹{roundRupees(getShopDisplayUnitPrice(selectedItem, 'retail')).toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('orderModal.stockLabel')} {selectedItem.currentStock} {t('orderModal.available')}</p>
                </div>
              </div>

              {/* Custom request */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-foreground mb-1">{t('orderModal.customRequest')}</label>
                <textarea
                  value={itemForm.customRequest}
                  onChange={(e) => setItemForm(prev => ({ ...prev, customRequest: e.target.value }))}
                  placeholder={t('orderModal.customPlaceholder')}
                  className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  rows="1"
                />
              </div>

              {/* Cart hint */}
              <div className="rounded-lg p-2 bg-secondary border border-border mb-3">
                <p className="text-xs text-secondary-foreground leading-snug">
                  <strong>{t('orderModal.cartHintTitle')}</strong>{' '}
                  {t('orderModal.cartHintBody')}
                </p>
              </div>

              {/* Add to Cart — sticky at bottom */}
              <div className="sticky bottom-0 bg-card pt-2 pb-4 sm:pb-0 sm:static">
                <button
                  onClick={handleAddToCart}
                  className={`w-full py-3 px-6 ${uiTokens.buttonPrimary}`}
                >
                  {t('orderModal.addToCart')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-2 backdrop-blur-sm sm:p-4">
          <div className="w-full max-w-md max-h-[95dvh] sm:max-h-[95vh] overflow-y-auto rounded-xl border border-border bg-card text-card-foreground shadow-2xl">
            <div className="p-3 sm:p-4">
              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <h2 className="text-lg font-bold text-foreground pr-2">
                  {t('checkoutModal.title')}
                </h2>
                <button
                  onClick={() => setShowCheckoutModal(false)}
                  className="text-muted-foreground hover:text-foreground text-xl flex-shrink-0 p-1 hover:bg-muted rounded"
                >
                  ✕
                </button>
              </div>

              {/* Order Summary */}
              <div className="bg-muted p-3 rounded-lg mb-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold">{t('checkoutModal.summaryTitle')}</h3>
                  <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold capitalize text-blue-800">
                    {t('checkoutModal.orderTypeRetail')}
                  </span>
                </div>
                <div className="space-y-1">
                  {cart.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>{itemName(item)} × {item.quantity}</span>
                      <span>₹{roundRupees(cartLineUnitPrice(item) * item.quantity).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                  <div className="border-t pt-1 mt-1">
                    <div className="flex justify-between text-sm font-semibold">
                      <span>{t('checkoutModal.totalAmount')}</span>
                      <span>₹{calculateGrandTotal().toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Checkout Form */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t('checkoutModal.customRequest')}</label>
                  <textarea
                    value={checkoutForm.customRequest}
                    onChange={(e) => setCheckoutForm(prev => ({ ...prev, customRequest: e.target.value }))}
                    placeholder={t('orderModal.customPlaceholder')}
                    className="w-full rounded-md border border-input bg-card px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    rows={1}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t('checkoutModal.fullName')}</label>
                  <input
                    type="text"
                    value={checkoutForm.customerName}
                    onChange={(e) => setCheckoutForm(prev => ({ ...prev, customerName: e.target.value }))}
                    className="w-full rounded-md border border-input bg-card px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder={t('checkoutModal.namePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t('checkoutModal.phone')}</label>
                  <input
                    type="tel"
                    value={checkoutForm.phone}
                    onChange={(e) => setCheckoutForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full rounded-md border border-input bg-card px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder={t('checkoutModal.phonePlaceholder')}
                    pattern="[6-9][0-9]{9}"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">{t('checkoutModal.deliveryDate')}</label>
                  <input
                    type="date"
                    value={checkoutForm.deliveryDate}
                    onChange={(e) => setCheckoutForm(prev => ({ ...prev, deliveryDate: e.target.value }))}
                    className="w-full rounded-md border border-input bg-card px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                {shopPaymentEnabled ? (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t('checkoutModal.advanceLabel')}</label>
                    <input
                      type="number"
                      min="1"
                      max={calculateGrandTotal()}
                      value={checkoutForm.advanceAmount}
                      onChange={(e) => setCheckoutForm(prev => ({ ...prev, advanceAmount: roundRupees(parseFloat(e.target.value) || 0) }))}
                      className="w-full rounded-md border border-input bg-card px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                      {t('checkoutModal.advanceHint')} {t('checkoutModal.balanceDue')} ₹{(calculateGrandTotal() - checkoutForm.advanceAmount).toLocaleString('en-IN')}
                    </p>
                  </div>
                ) : null}

                <div className="bg-secondary border border-border p-2">
                  {shopPaymentEnabled ? (
                    <>
                      <p className="text-xs text-secondary-foreground whitespace-pre-line">
                        {t('checkoutModal.paymentBlurb', {
                          advance: checkoutForm.advanceAmount.toLocaleString('en-IN'),
                        })}
                      </p>
                      {razorpayTestMode ? (
                        <p className="text-xs text-muted-foreground mt-1 border-t border-border/60 pt-1">
                          {t('checkoutModal.testPaymentHint')}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-secondary-foreground">{t('checkoutModal.paymentDisabledHint')}</p>
                      <p className="text-xs text-secondary-foreground mt-1 whitespace-pre-line">{t('checkoutModal.inquiryBlurb')}</p>
                    </>
                  )}
                </div>

                <button
                  onClick={handleCheckoutSubmit}
                  disabled={simulatingPayment || submittingInquiry || shopPaymentEnabled === null}
                  className={`w-full py-2.5 px-6 ${uiTokens.buttonPrimary}`}
                >
                  {shopPaymentEnabled
                    ? t('checkoutModal.proceedPayment')
                    : t('checkoutModal.submitInquiry')}
                </button>
                {shopPaymentEnabled && razorpayTestMode ? (
                  <button
                    type="button"
                    onClick={handleSimulateTestPayment}
                    disabled={simulatingPayment || submittingInquiry}
                    className={`w-full py-2.5 px-6 ${uiTokens.buttonGhost} border border-border text-sm`}
                  >
                    {simulatingPayment ? 'Processing…' : t('checkoutModal.simulateTestPayment')}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wishlist Modal (temporarily disabled UI) */}
      {false && showWishlistModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-2xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-foreground">
                  My Wishlist ❤️ ({wishlist.length})
                </h2>
                <button
                  onClick={() => setShowWishlistModal(false)}
                  className="text-muted-foreground hover:text-foreground text-2xl p-1 hover:bg-muted rounded"
                >
                  ✕
                </button>
              </div>

              {/* Wishlist Content */}
              {wishlist.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🤍</div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Your wishlist is empty</h3>
                  <p className="text-muted-foreground mb-6">Start browsing and add items you love!</p>
                  <button
                    onClick={() => setShowWishlistModal(false)}
                    className={`px-6 py-2 ${uiTokens.buttonPrimary}`}
                  >
                    {t('productDetail.continueShopping')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto max-h-96">
                  {wishlist.map(itemId => {
                    const item = inventory.find(inv => inv.id === itemId);
                    if (!item) return null;

                    return (
                      <div
                        key={itemId}
                        className="overflow-hidden rounded-lg border border-border bg-card transition-shadow hover:shadow-lg"
                      >
                        {/* Image */}
                        <div className="relative aspect-square overflow-hidden">
                          <img
                            src={item.photo}
                            alt={itemName(item)}
                            className="w-full h-full object-cover"
                          />
                          {item.retailOnly && (
                            <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
                              {t('gallery.retailOnlyBadge')}
                            </div>
                          )}
                          {/* Remove from Wishlist */}
                          <button
                            onClick={() => {
                              setWishlist(prev => prev.filter(id => id !== itemId));
                              toast.success(t('toast.wishlistRemoved'));
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                            title="Remove from wishlist"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>

                        {/* Details */}
                        <div className="p-4">
                          <div className="mb-2">
                            <span className="text-xs text-muted-foreground uppercase tracking-wide">{t('gallery.modelPrefix')} {formatModelCode(itemSize(item), item.modelNumber)}</span>
                          </div>
                          <h3 className="font-bold text-lg mb-2 text-foreground line-clamp-2">{itemName(item)}</h3>
                          <div className="flex items-center justify-between">
                            <p className="text-2xl font-bold text-orange-600">₹{roundRupees(item.price).toLocaleString('en-IN')}</p>
                            <button
                              onClick={() => {
                                setShowWishlistModal(false);
                                openAddToCartModal(item);
                              }}
                              className={`px-4 py-2 text-sm ${uiTokens.buttonPrimary}`}
                            >
                              Add to Cart
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sticky Mobile Mini Cart */}
      {cart.length > 0 && !showCheckoutModal && !showModal && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/95 backdrop-blur-md shadow-[0_-6px_20px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3 flex items-center justify-between gap-3">
            <button
              onClick={() => setCartExpanded((prev) => !prev)}
              className={`flex-1 text-left px-3 py-2 ${uiTokens.buttonGhost}`}
            >
              <p className="text-sm font-semibold text-foreground">
                {t(cart.length === 1 ? 'cart.itemsInCart_one' : 'cart.itemsInCart_other', { count: cart.length })}
              </p>
              <p className="text-xs text-muted-foreground">
                ₹{calculateGrandTotal().toLocaleString('en-IN')}
              </p>
            </button>
            <button
              onClick={handleCheckout}
              className={`px-4 py-2.5 whitespace-nowrap ${uiTokens.buttonPrimary}`}
            >
              {shopPaymentEnabled === true
                ? t('cart.checkoutSticky')
                : t('cart.checkoutStickyInquiry')}
            </button>
          </div>
        </div>
      )}

    </>
  );
};

export default ShopPage;
